import { OverallStatsIcon } from '@forwardflow/assets/svg/OverallStatsIcon'
import { useGetDashboardCommunication } from '@forwardflow/hooks/customHooks'
import { AgentType, TimePeriod } from '@forwardflow/types'
import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'

const chartColors = ['#3A8DFF', '#2ED9C3', '#F98D56', '#FACF71']

export const OverallStatsCard = () => {
   const [searchParams] = useSearchParams()
   const timePeriod = searchParams.get('timePeriod') || 'allTime'
   const agentType = searchParams.get('agentType') || 'all'
   const {
      data: DashboardChartsLeadSource,
      isLoading: DashboardChartsLeadSourceLoading,
   } = useGetDashboardCommunication({
      timePeriod: timePeriod as TimePeriod,
      agentType: agentType as AgentType,
   })
   const [hoveredIndex, setHoveredIndex] = useState<number | null>(null)

   const statsData =
      DashboardChartsLeadSource?.overallStats?.map((item, idx) => ({
         name: item.label,
         percent: item.percentage,
         count: item.value,
         change: `${item.change.isIncrement ? '+' : '-'}${item.change.value}%`,
         color: chartColors[idx % chartColors.length],
      })) || []

   return (
      <div className="gap-8 px-6 py-4 bg-white/5 rounded-xl">
         <div className="flex items-center gap-4 mb-3">
            <OverallStatsIcon />
            <span className="font-medium text-white">Overall Stats</span>
         </div>
         <div className="flex">
            <div className="flex flex-col items-center justify-center md:w-2/5">
               <div className="w-full h-full max-h-[240px]">
                  {DashboardChartsLeadSourceLoading ? (
                     <div className="text-center text-white">Loading...</div>
                  ) : (
                     <PieChart
                        data={statsData}
                        hoveredIndex={hoveredIndex}
                        setHoveredIndex={setHoveredIndex}
                     />
                  )}
               </div>
            </div>
            <div className="flex flex-col justify-center gap-3 md:w-3/5">
               {DashboardChartsLeadSourceLoading
                  ? Array(4)
                       .fill(0)
                       .map((_, idx) => (
                          <div
                             key={idx}
                             className="flex items-center justify-between px-5 py-3 rounded-lg min-h-12 bg-white/10 animate-pulse"
                          >
                             <div className="flex items-center gap-3">
                                <span className="w-3 h-3 bg-gray-400 rounded-full" />
                                <span className="text-sm text-gray-400">
                                   Loading...
                                </span>
                             </div>
                             <div className="flex items-center gap-4">
                                <span className="text-sm font-semibold text-gray-400">
                                   --%
                                </span>
                                <span className="text-sm text-gray-400">
                                   --
                                </span>
                                <span className="text-sm text-gray-400">
                                   --
                                </span>
                             </div>
                          </div>
                       ))
                  : statsData.map((item, idx) => (
                       <div
                          key={item.name}
                          className={`flex items-center min-h-12 justify-between px-5 py-3 rounded-lg transition-all cursor-pointer font-medium`}
                          style={{
                             background:
                                hoveredIndex === idx
                                   ? 'rgba(255,255,255,0.06)'
                                   : 'rgba(255,255,255,0.03)',
                             opacity:
                                hoveredIndex === null
                                   ? 1
                                   : hoveredIndex === idx
                                   ? 1
                                   : 0.5,
                          }}
                          onMouseEnter={() => setHoveredIndex(idx)}
                          onMouseLeave={() => setHoveredIndex(null)}
                       >
                          <div className="flex items-center gap-3">
                             <span
                                className="w-3 h-3 rounded-full"
                                style={{ backgroundColor: item.color }}
                             />
                             <span className="text-sm text-white">
                                {item.name}
                             </span>
                          </div>
                          <div className="flex items-center gap-4">
                             <span
                                className="text-sm font-semibold"
                                style={{ color: item.color }}
                             >
                                {item.percent}%
                             </span>
                             <span className="text-sm text-white">
                                {item.count}
                             </span>
                             <span
                                className={`text-sm ${
                                   item.change.startsWith('+')
                                      ? 'text-green-400'
                                      : 'text-red-400'
                                }`}
                             >
                                {item.change}
                             </span>
                          </div>
                       </div>
                    ))}
            </div>
         </div>
      </div>
   )
}

type ChartDataItem = {
   name: string
   percent: number
   count: number
   change: string
   color: string
}

const PieChart = ({
   data,
   hoveredIndex,
   setHoveredIndex,
}: {
   data: ChartDataItem[]
   hoveredIndex: number | null
   setHoveredIndex: (i: number | null) => void
}) => {
   const centerX = 100
   const centerY = 100
   const outerRadius = 90
   const innerRadius = 72

   let currentAngle = -90
   const segments = data.map((item, index) => {
      const angle = (item.percent / 100) * 360
      const startAngle = currentAngle
      const endAngle = currentAngle + angle
      currentAngle = endAngle

      const startRad = (startAngle * Math.PI) / 180
      const endRad = (endAngle * Math.PI) / 180

      const x1 = centerX + outerRadius * Math.cos(startRad)
      const y1 = centerY + outerRadius * Math.sin(startRad)
      const x2 = centerX + outerRadius * Math.cos(endRad)
      const y2 = centerY + outerRadius * Math.sin(endRad)
      const x3 = centerX + innerRadius * Math.cos(endRad)
      const y3 = centerY + innerRadius * Math.sin(endRad)
      const x4 = centerX + innerRadius * Math.cos(startRad)
      const y4 = centerY + innerRadius * Math.sin(startRad)

      const largeArcFlag = angle > 180 ? 1 : 0

      const path = `
         M ${x1} ${y1}
         A ${outerRadius} ${outerRadius} 0 ${largeArcFlag} 1 ${x2} ${y2}
         L ${x3} ${y3}
         A ${innerRadius} ${innerRadius} 0 ${largeArcFlag} 0 ${x4} ${y4}
         Z
      `
      return { path, color: item.color, index }
   })

   return (
      <svg viewBox="0 0 200 200" className="w-full h-full">
         {segments.map((seg, idx) => (
            <path
               key={idx}
               d={seg.path}
               fill={seg.color}
               opacity={
                  hoveredIndex === null ? 1 : hoveredIndex === idx ? 1 : 0.4
               }
               style={{ transition: 'opacity 0.2s', cursor: 'pointer' }}
               onMouseEnter={() => setHoveredIndex(idx)}
               onMouseLeave={() => setHoveredIndex(null)}
            />
         ))}
      </svg>
   )
}
